import { useEffect, useMemo, useState } from "react";
import {
  Plus, Trash2, Search, Pencil, Download, ClipboardCheck, Clock, Coins, Users,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";

const today = () => new Date().toISOString().slice(0, 10);
const startOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

const emptyForm = {
  employee_id: "",
  employee_name: "",
  project_id: "",
  project_name: "",
  date: today(),
  shift_hours: 8,
  status: "present",
  overtime_amount: 0,
  notes: "",
};

const STATUS_OPTIONS = [
  { v: "present", label: "Present" },
  { v: "dayoff_worked", label: "Day-off worked" },
  { v: "leave", label: "Leave" },
  { v: "absent", label: "Absent" },
];

const SHIFT_OPTIONS = [8, 10, 12];

export default function AttendancePage() {
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState({ totals: {}, per_employee: [], per_project: [] });
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState(startOfMonth());
  const [endDate, setEndDate] = useState(today());
  const [projectFilter, setProjectFilter] = useState("all");

  const loadAll = async () => {
    try {
      const params = `?start_date=${startDate}&end_date=${endDate}${projectFilter !== "all" ? `&project_id=${projectFilter}` : ""}`;
      const [recRes, sumRes] = await Promise.all([
        api.get(`/admin/attendance${params}`),
        api.get(`/admin/attendance/summary?start_date=${startDate}&end_date=${endDate}`),
      ]);
      setRecords(recRes.data?.items || []);
      setSummary(sumRes.data || {});
    } catch {
      toast.error("Failed to load attendance");
    }
  };

  const loadRefs = async () => {
    try {
      const [empRes, projRes] = await Promise.all([
        api.get("/admin/employees"),
        api.get("/admin/projects"),
      ]);
      setEmployees(empRes.data?.items || []);
      setProjects(projRes.data || []);
    } catch { /* refs optional */ }
  };

  useEffect(() => { loadRefs(); }, []);
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [startDate, endDate, projectFilter]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, date: today() });
    setOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r.id);
    setForm({ ...emptyForm, ...r });
    setOpen(true);
  };

  const save = async () => {
    if (!form.employee_name.trim() || !form.project_name.trim() || !form.date) {
      toast.error("Employee, project and date are required");
      return;
    }
    try {
      const payload = {
        ...form,
        shift_hours: parseInt(form.shift_hours, 10) || 8,
        overtime_amount: parseFloat(form.overtime_amount) || 0,
      };
      if (editing) {
        await api.put(`/admin/attendance/${editing}`, payload);
        toast.success("Attendance updated");
      } else {
        await api.post("/admin/attendance", payload);
        toast.success("Attendance marked");
      }
      setOpen(false);
      setForm(emptyForm);
      setEditing(null);
      loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this attendance record?")) return;
    try {
      await api.delete(`/admin/attendance/${id}`);
      toast.success("Removed");
      loadAll();
    } catch { toast.error("Could not remove"); }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return records.filter((r) =>
      !q ||
      r.employee_name.toLowerCase().includes(q) ||
      r.project_name.toLowerCase().includes(q) ||
      (r.notes || "").toLowerCase().includes(q),
    );
  }, [records, search]);

  const exportCsv = () => {
    const headers = ["Date", "Employee", "Project", "Shift (hrs)", "Status", "Overtime SAR", "Notes"];
    const rows = filtered.map((r) => [
      r.date, r.employee_name, r.project_name, r.shift_hours,
      r.status, r.overtime_amount, (r.notes || "").replace(/[\r\n,]+/g, " "),
    ]);
    const csv = [headers, ...rows].map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `mnh-attendance-${startDate}-to-${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // When employee/project selected from dropdown, fill the name + id
  const pickEmployee = (id) => {
    const e = employees.find((x) => x.id === id);
    setForm((f) => ({ ...f, employee_id: id, employee_name: e?.full_name || f.employee_name }));
  };
  const pickProject = (id) => {
    const p = projects.find((x) => x.id === id);
    setForm((f) => ({ ...f, project_id: id, project_name: p?.name || f.project_name }));
  };

  const totals = summary.totals || {};

  return (
    <div className="space-y-8" data-testid="attendance-page">
      <header className="flex flex-wrap justify-between items-end gap-4">
        <div>
          <span className="overline">/ Workforce Attendance</span>
          <h1 className="mt-3 text-3xl sm:text-4xl font-black">
            Attendance <span className="text-primary">Sheet</span>
          </h1>
          <p className="text-muted-foreground mt-2">
            Mark daily duty hours, overtime bonus and day-off worked — by project.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportCsv} className="rounded-sm h-11" data-testid="att-export-csv">
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
          <Button
            onClick={openCreate}
            className="rounded-sm h-11 bg-secondary hover:bg-primary text-secondary-foreground hover:text-primary-foreground"
            data-testid="att-add-btn"
          >
            <Plus className="h-4 w-4 mr-2" /> Mark Attendance
          </Button>
        </div>
      </header>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <SumCard icon={ClipboardCheck} label="Records" value={totals.records || 0} testid="att-stat-records" />
        <SumCard icon={Clock} label="Total Hours" value={totals.total_hours || 0} testid="att-stat-hours" />
        <SumCard icon={Coins} label="Overtime (SAR)" value={(totals.total_overtime_sar || 0).toLocaleString()} testid="att-stat-overtime" tone="primary" />
        <SumCard icon={Users} label="Unique Workers" value={totals.unique_employees || 0} testid="att-stat-workers" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <Label className="text-xs uppercase tracking-[0.18em] mb-1 block">From</Label>
          <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="rounded-sm h-11 w-44" data-testid="att-filter-start" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.18em] mb-1 block">To</Label>
          <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="rounded-sm h-11 w-44" data-testid="att-filter-end" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.18em] mb-1 block">Project</Label>
          <Select value={projectFilter} onValueChange={setProjectFilter}>
            <SelectTrigger className="rounded-sm h-11 w-64" data-testid="att-filter-project"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All projects</SelectItem>
              {projects.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <Label className="text-xs uppercase tracking-[0.18em] mb-1 block">Search</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search employee, project, notes…" value={search} onChange={(e) => setSearch(e.target.value)} className="rounded-sm h-11 pl-9" data-testid="att-search" />
          </div>
        </div>
      </div>

      <Tabs defaultValue="daily" className="w-full">
        <TabsList className="rounded-sm">
          <TabsTrigger value="daily" data-testid="att-tab-daily">Daily Log</TabsTrigger>
          <TabsTrigger value="by-employee" data-testid="att-tab-employee">Per Employee</TabsTrigger>
          <TabsTrigger value="by-project" data-testid="att-tab-project">Per Project</TabsTrigger>
        </TabsList>

        {/* Daily log */}
        <TabsContent value="daily">
          <Card className="rounded-sm overflow-hidden">
            <Table data-testid="att-table">
              <TableHeader>
                <TableRow className="bg-muted/60">
                  <TableHead>Date</TableHead>
                  <TableHead>Employee</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Shift</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-end">Overtime (SAR)</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-12">
                    No attendance recorded for this period.
                  </TableCell></TableRow>
                )}
                {filtered.map((r) => (
                  <TableRow key={r.id} data-testid={`att-row-${r.id}`}>
                    <TableCell className="font-mono text-xs">{r.date}</TableCell>
                    <TableCell className="font-semibold">{r.employee_name}</TableCell>
                    <TableCell className="text-muted-foreground">{r.project_name}</TableCell>
                    <TableCell><Badge variant="outline" className="rounded-sm">{r.shift_hours} hrs</Badge></TableCell>
                    <TableCell><StatusPill status={r.status} /></TableCell>
                    <TableCell className="text-end font-mono tabular-nums">
                      {r.overtime_amount > 0
                        ? <span className="text-primary font-bold">+{r.overtime_amount.toLocaleString()}</span>
                        : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 justify-end">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(r)} data-testid={`att-edit-${r.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => remove(r.id)} data-testid={`att-delete-${r.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Per employee */}
        <TabsContent value="by-employee">
          <Card className="rounded-sm overflow-hidden">
            <Table data-testid="att-table-employee">
              <TableHeader>
                <TableRow className="bg-muted/60">
                  <TableHead>Employee</TableHead>
                  <TableHead className="text-end">Days Present</TableHead>
                  <TableHead className="text-end">Day-off Worked</TableHead>
                  <TableHead className="text-end">Absent</TableHead>
                  <TableHead className="text-end">Total Hours</TableHead>
                  <TableHead className="text-end">Overtime (SAR)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(summary.per_employee || []).length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-12">No data.</TableCell></TableRow>
                )}
                {(summary.per_employee || []).map((e, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-semibold">{e.employee_name}</TableCell>
                    <TableCell className="text-end tabular-nums">{e.days_present}</TableCell>
                    <TableCell className="text-end tabular-nums">{e.days_off_worked}</TableCell>
                    <TableCell className="text-end tabular-nums text-muted-foreground">{e.days_absent}</TableCell>
                    <TableCell className="text-end tabular-nums font-semibold">{e.total_hours}</TableCell>
                    <TableCell className="text-end tabular-nums text-primary font-bold">
                      {e.total_overtime_sar > 0 ? `+${e.total_overtime_sar.toLocaleString()}` : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Per project */}
        <TabsContent value="by-project">
          <Card className="rounded-sm overflow-hidden">
            <Table data-testid="att-table-project">
              <TableHeader>
                <TableRow className="bg-muted/60">
                  <TableHead>Project</TableHead>
                  <TableHead className="text-end">Workers</TableHead>
                  <TableHead className="text-end">Total Hours</TableHead>
                  <TableHead className="text-end">Overtime (SAR)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(summary.per_project || []).length === 0 && (
                  <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-12">No data.</TableCell></TableRow>
                )}
                {(summary.per_project || []).map((p, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-semibold">{p.project_name}</TableCell>
                    <TableCell className="text-end tabular-nums">{p.unique_workers}</TableCell>
                    <TableCell className="text-end tabular-nums font-semibold">{p.total_hours}</TableCell>
                    <TableCell className="text-end tabular-nums text-primary font-bold">
                      {p.total_overtime_sar > 0 ? `+${p.total_overtime_sar.toLocaleString()}` : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Mark dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-2xl" data-testid="att-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit attendance" : "Mark attendance"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-[0.18em] mb-1 block">Employee *</Label>
              {employees.length > 0 ? (
                <Select value={form.employee_id || ""} onValueChange={pickEmployee}>
                  <SelectTrigger className="rounded-sm" data-testid="att-select-employee"><SelectValue placeholder="Pick from workforce" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {employees.map((e) => (<SelectItem key={e.id} value={e.id}>{e.full_name} · {e.role}</SelectItem>))}
                  </SelectContent>
                </Select>
              ) : null}
              <Input
                placeholder="Or type employee name"
                value={form.employee_name}
                onChange={(e) => setForm({ ...form, employee_name: e.target.value })}
                className="rounded-sm mt-2"
                data-testid="att-input-employee_name"
              />
            </div>

            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-[0.18em] mb-1 block">Project *</Label>
              {projects.length > 0 ? (
                <Select value={form.project_id || ""} onValueChange={pickProject}>
                  <SelectTrigger className="rounded-sm" data-testid="att-select-project"><SelectValue placeholder="Pick a project" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {projects.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name} · {p.location}</SelectItem>))}
                  </SelectContent>
                </Select>
              ) : null}
              <Input
                placeholder="Or type project / site name"
                value={form.project_name}
                onChange={(e) => setForm({ ...form, project_name: e.target.value })}
                className="rounded-sm mt-2"
                data-testid="att-input-project_name"
              />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-[0.18em] mb-1 block">Date *</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-sm" data-testid="att-input-date" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] mb-1 block">Shift hours</Label>
              <Select value={String(form.shift_hours)} onValueChange={(v) => setForm({ ...form, shift_hours: parseInt(v, 10) })}>
                <SelectTrigger className="rounded-sm" data-testid="att-input-shift_hours"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SHIFT_OPTIONS.map((h) => (<SelectItem key={h} value={String(h)}>{h} hours</SelectItem>))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-[0.18em] mb-1 block">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="rounded-sm" data-testid="att-input-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((o) => (<SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] mb-1 block">Overtime bonus (SAR)</Label>
              <Input
                type="number"
                step="1"
                min="0"
                value={form.overtime_amount}
                onChange={(e) => setForm({ ...form, overtime_amount: e.target.value })}
                className="rounded-sm"
                data-testid="att-input-overtime_amount"
                placeholder="e.g. 50"
              />
            </div>

            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-[0.18em] mb-1 block">Notes</Label>
              <Textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="rounded-sm"
                data-testid="att-input-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={save} className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90" data-testid="att-save-btn">
              {editing ? "Save changes" : "Mark attendance"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SumCard({ icon: Icon, label, value, tone, testid }) {
  return (
    <div
      className={`p-5 rounded-sm border ${tone === "primary" ? "bg-primary/[0.06] border-primary/30" : "bg-card border-border"}`}
      data-testid={testid}
    >
      <div className="flex items-start justify-between">
        <Icon className={`h-5 w-5 ${tone === "primary" ? "text-primary" : "text-muted-foreground"}`} />
        <div className={`text-3xl font-black tabular-nums ${tone === "primary" ? "text-primary" : ""}`}>{value}</div>
      </div>
      <div className="mt-3 text-xs uppercase tracking-[0.18em] font-semibold">{label}</div>
    </div>
  );
}

function StatusPill({ status }) {
  if (status === "present") return <Badge className="rounded-sm bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-emerald-200">Present</Badge>;
  if (status === "dayoff_worked") return <Badge className="rounded-sm bg-amber-100 text-amber-800 hover:bg-amber-100 border-amber-200">Day-off worked</Badge>;
  if (status === "leave") return <Badge variant="outline" className="rounded-sm">Leave</Badge>;
  if (status === "absent") return <Badge className="rounded-sm bg-red-100 text-red-700 hover:bg-red-100 border-red-200">Absent</Badge>;
  return <Badge variant="outline" className="rounded-sm">{status}</Badge>;
}
